<template>
  <v-row>
    <template v-if="areaVideos.length">
      <v-col v-if="props.area.guide?.active" cols="6" sm="4" md="3">
        <router-link :to="`/videos/${subject?.slug}/${area.slug}`" class="text-decoration-none">
          <v-img cover :src="subjectBackground" :aspect-ratio="16 / 9" :class="`bg-subject mb-3`" alt="">
            <div class="d-inline-flex justify-center align-center h-100 my-0 w-100">
              <FontAwesomeIcon size="sm" class="mr-2" icon="fa-solid fa-rocket-launch" />
              <span
                class="area-guide-thumbnail-title d-inline-flex justify-center align-center my-0 font-weight-medium"
              >
                {{ t(l.area_guide.go_to) }} ...
              </span>
            </div>
          </v-img>
          <div class="area-guide-thumbnail-subtitle underline text-secondary">
            {{ `... ${t(l.with)} ${area.name.toLowerCase()}` }}
          </div>
        </router-link>
      </v-col>
      <VideoThumbnail
        v-for="(video, index) in areaVideos"
        :key="index"
        :subject-id="subjectId"
        :video="video"
        :area="props.area"
        :use-theme-styling="false"
      />
    </template>
    <template v-else>
      <v-col v-for="n in 4" :key="n">
        <v-skeleton-loader type="image"></v-skeleton-loader>
      </v-col>
    </template>
  </v-row>
</template>

<script setup lang="ts">
import type { Area } from '@ilteducation/content-ref-data-types';
import { computed } from 'vue';
import { Video } from '@ilteducation/videos';
import { useVideos } from '@ilteducation/videos';
import { useCategories } from '@ilteducation/content-ref-data';
import { useLocale } from '@/plugins/translation';
import VideoThumbnail from './VideoThumbnail.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

const props = defineProps<{
  videos: Video[];
  area: Area;
  subjectId: string;
}>();

const { t, l } = useLocale();
const { categories } = useCategories();
const subject = computed(() => categories.value.find((category) => category.id === props.subjectId));

const subjectBackground = computed(
  () => new URL(`/src/assets/images/patterns/square/${subject.value?.color}.png`, import.meta.url).href,
);

const areaVideos = computed(() =>
  props.videos
    .filter((video) => video.orderedAreas.flatMap((area) => area.id).includes(props.area.id))
    .sort(useVideos().sortInArea(props.area.id)),
);

const isReady = computed(() => areaVideos.value.length > 0);

defineExpose({ isReady });
</script>
<style scoped lang="scss">
@use '@/foundations/theme/settings';
@use 'sass:map';

.area-guide-thumbnail {
  &-title {
    font-family: settings.$heading-font;
    font-size: 1.125rem;
    @media #{map.get(settings.$display-breakpoints, 'lg-and-up')} {
      font-size: 1.5rem !important;
    }
  }
  &-subtitle {
    font-family: settings.$heading-font;
    font-size: 0.875rem !important;
    letter-spacing: 0.1px;
    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      font-size: 0.9375rem !important;
      letter-spacing: 0;
    }

    &.underline {
      text-decoration: none;
      border-bottom: 3px solid rgba(var(--v-theme-primary), 0.1);
      display: inline;
      line-height: 1.6;
      transition: all 0.1s ease;
    }
  }
}
</style>
